import React from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { API } from "../config";

const Contact = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Contact Us"
        description="Get in touch with Lloyd Funding. Apply for business funding or ask about our financing solutions. We respond within minutes."
        canonical="/contact"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-7xl px-4 pt-20 pb-12 sm:px-6 sm:pt-24 sm:pb-12 lg:px-8 lg:pt-24 lg:pb-16">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl lg:text-5xl">
              Get in <span className="text-blue-700">Touch</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Ready to get funded? Fill out the form below and our team will get
              back to you within minutes.
            </p>
          </div>
        </div>

        {/* Wave divider */}
        <div className="pointer-events-none relative h-10 overflow-hidden">
          <svg
            className="absolute bottom-0 left-0 h-16 w-full"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,64 C288,110 576,110 864,80 C1152,50 1296,50 1440,80 L1440,120 L0,120 Z"
              fill="#1E40AF"
            />
          </svg>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="relative bg-blue-800 overflow-hidden">
        {/* Decorative semicircles */}
        <div className="pointer-events-none absolute -bottom-64 -left-64 h-[512px] w-[512px] rounded-full bg-blue-700/50" />

        <div className="relative mx-auto max-w-7xl px-4 pt-12 pb-12 sm:px-6 lg:px-8 lg:pt-20 lg:pb-20">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 lg:items-start">
            {/* Left side - Info */}
            <div className="flex flex-col justify-center lg:py-8">
              <h2 className="text-2xl font-bold text-white sm:text-3xl lg:text-4xl">
                Let's talk about your funding needs
              </h2>
              <p className="mt-3 text-lg text-blue-100">
                Our funding experts are standing by to help you find the perfect
                financing solution for your business.
              </p>

              {/* Contact Info */}
              <div className="mt-10 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-200">Call us</p>
                    <p className="text-lg font-semibold text-white">
                      (786) 321-9346
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-200">
                      Business hours
                    </p>
                    <p className="text-lg font-semibold text-white">
                      Mon - Fri, 10AM - 6PM EST
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-200">
                      Email us
                    </p>
                    <p className="text-lg font-semibold text-white break-all sm:break-normal">
                      submissions@lloydfunding.com
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-200">
                      Our office
                    </p>
                    <p className="text-lg font-semibold text-white">
                      8400 NW 33rd St, Suite 310
                    </p>
                    <p className="text-sm text-blue-200">Miami, FL 33122</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Form */}
            <div
              id="application-form-desktop"
              className="hidden lg:block scroll-mt-24"
            >
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.target;
                  const formData = new FormData(form);

                  const data = {
                    firstName: formData.get("firstName"),
                    lastName: formData.get("lastName"),
                    email: formData.get("email"),
                    phone: formData.get("phone"),
                    company: formData.get("company"),
                    revenue: formData.get("revenue"),
                    amount: formData.get("amount"),
                  };

                  try {
                    const response = await fetch(
                      API.CONTACT_FORM,
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify(data),
                      }
                    );

                    if (response.ok) {
                      navigate("/thank-you");
                    } else {
                      throw new Error("Failed to submit");
                    }
                  } catch (error) {
                    alert("Oops! Something went wrong. Please try again.");
                  }
                }}
                className="rounded-2xl bg-white p-6 sm:p-8 shadow-xl"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {/* First Name */}
                  <div>
                    <label
                      htmlFor="firstNameDesktop"
                      className="block text-sm font-medium text-slate-700"
                    >
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstNameDesktop"
                      name="firstName"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label
                      htmlFor="lastNameDesktop"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastNameDesktop"
                      name="lastName"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="emailDesktop"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="emailDesktop"
                      name="email"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phoneDesktop"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phoneDesktop"
                      name="phone"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                    />
                  </div>

                  {/* Company Name */}
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="companyDesktop"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="companyDesktop"
                      name="company"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                    />
                  </div>

                  {/* Monthly Revenue */}
                  <div>
                    <label
                      htmlFor="revenueDesktop"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Monthly Revenue
                    </label>
                    <div className="relative">
                      <select
                        id="revenueDesktop"
                        name="revenue"
                        required
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 pr-10 text-slate-900 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
                      >
                        <option value="">Please Select</option>
                        <option value="5k-25k">$5K - $25K</option>
                        <option value="25k-50k">$25K - $50K</option>
                        <option value="50k-100k">$50K - $100K</option>
                        <option value="100k-250k">$100K - $250K</option>
                        <option value="250k-500k">$250K - $500K</option>
                        <option value="500k+">Over $500K</option>
                      </select>
                      <svg
                        className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m19 9-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Amount Requested */}
                  <div>
                    <label
                      htmlFor="amountDesktop"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Amount Requested
                    </label>
                    <div className="relative">
                      <select
                        id="amountDesktop"
                        name="amount"
                        required
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 pr-10 text-slate-900 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
                      >
                        <option value="">Please Select</option>
                        <option value="5k-25k">$5K - $25K</option>
                        <option value="25k-50k">$25K - $50K</option>
                        <option value="50k-100k">$50K - $100K</option>
                        <option value="100k-250k">$100K - $250K</option>
                        <option value="250k-500k">$250K - $500K</option>
                        <option value="500k+">Over $500K</option>
                      </select>
                      <svg
                        className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m19 9-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="sm:col-span-2 pt-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="smsConsentDesktop"
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/20 flex-shrink-0"
                      />
                      <span className="text-xs leading-relaxed text-slate-500">
                        By submitting this form, I consent to receive text
                        messages and emails from Lloyd Funding regarding my
                        application and funding options. Message frequency
                        varies. Message and data rates may apply. Reply HELP for
                        support. Reply STOP to opt out. View our{" "}
                        <Link
                          to="/privacy-policy"
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          Privacy Policy
                        </Link>{" "}
                        and{" "}
                        <Link
                          to="/terms-of-service"
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          Terms of Service
                        </Link>
                        .
                      </span>
                    </label>
                    <button
                      type="submit"
                      className="mt-3 w-full rounded-xl bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/30 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95"
                    >
                      Submit Application
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section (mobile/tablet only) */}
      <section
        id="application-form"
        className="relative bg-blue-800 overflow-hidden lg:hidden scroll-mt-32 sm:scroll-mt-24"
      >
        <div className="relative mx-auto max-w-7xl px-4 pb-12 sm:px-6">
          {/* Right side - Form */}
          <div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const formData = new FormData(form);

                const data = {
                  firstName: formData.get("firstName"),
                  lastName: formData.get("lastName"),
                  email: formData.get("email"),
                  phone: formData.get("phone"),
                  company: formData.get("company"),
                  revenue: formData.get("revenue"),
                  amount: formData.get("amount"),
                };

                try {
                  const response = await fetch(
                    API.CONTACT_FORM,
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify(data),
                    }
                  );

                  if (response.ok) {
                    navigate("/thank-you");
                  } else {
                    throw new Error("Failed to submit");
                  }
                } catch (error) {
                  alert("Oops! Something went wrong. Please try again.");
                }
              }}
              className="rounded-2xl bg-white p-6 sm:p-8 shadow-xl"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {/* First Name */}
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-slate-700"
                  >
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-slate-700"
                  >
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-700"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-slate-700"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>

                {/* Company Name */}
                <div className="sm:col-span-2">
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-slate-700"
                  >
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>

                {/* Monthly Revenue */}
                <div>
                  <label
                    htmlFor="revenue"
                    className="block text-sm font-medium text-slate-700"
                  >
                    Monthly Revenue
                  </label>
                  <div className="relative">
                    <select
                      id="revenue"
                      name="revenue"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 pr-10 text-slate-900 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
                    >
                      <option value="">Please Select</option>
                      <option value="5k-25k">$5K - $25K</option>
                      <option value="25k-50k">$25K - $50K</option>
                      <option value="50k-100k">$50K - $100K</option>
                      <option value="100k-250k">$100K - $250K</option>
                      <option value="250k-500k">$250K - $500K</option>
                      <option value="500k+">Over $500K</option>
                    </select>
                    <svg
                      className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m19 9-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                {/* Amount Requested */}
                <div>
                  <label
                    htmlFor="amount"
                    className="block text-sm font-medium text-slate-700"
                  >
                    Amount Requested
                  </label>
                  <div className="relative">
                    <select
                      id="amount"
                      name="amount"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 pr-10 text-slate-900 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
                    >
                      <option value="">Please Select</option>
                      <option value="5k-25k">$5K - $25K</option>
                      <option value="25k-50k">$25K - $50K</option>
                      <option value="50k-100k">$50K - $100K</option>
                      <option value="100k-250k">$100K - $250K</option>
                      <option value="250k-500k">$250K - $500K</option>
                      <option value="500k+">Over $500K</option>
                    </select>
                    <svg
                      className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m19 9-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="sm:col-span-2 pt-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="smsConsent"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/20 flex-shrink-0"
                    />
                    <span className="text-xs leading-relaxed text-slate-500">
                      By submitting this form, I consent to receive text
                      messages and emails from Lloyd Funding regarding my
                      application and funding options. Message frequency varies.
                      Message and data rates may apply. Reply HELP for support.
                      Reply STOP to opt out. View our{" "}
                      <Link
                        to="/privacy-policy"
                        className="text-blue-600 hover:text-blue-700 underline"
                      >
                        Privacy Policy
                      </Link>{" "}
                      and{" "}
                      <Link
                        to="/terms-of-service"
                        className="text-blue-600 hover:text-blue-700 underline"
                      >
                        Terms of Service
                      </Link>
                      .
                    </span>
                  </label>
                  <button
                    type="submit"
                    className="mt-3 w-full rounded-xl bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/30 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95"
                  >
                    Submit Application
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-800 sm:text-4xl">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "How fast can I get funded?",
                answer:
                  "Most applications are approved within hours, and funding can be deposited as soon as the same business day.",
              },
              {
                question: "Will applying affect my credit score?",
                answer:
                  "No. We perform a soft credit pull during the application process, which does not impact your credit score.",
              },
              {
                question: "What documents do I need?",
                answer:
                  "Typically, we just need your basic business information and recent bank statements. No extensive paperwork required.",
              },
              {
                question: "What if I have bad credit?",
                answer:
                  "We look at many factors beyond just credit score, including your business revenue and history. Many businesses with less-than-perfect credit qualify.",
              },
            ].map((faq, index) => (
              <details
                key={index}
                className="group rounded-2xl border border-slate-100 bg-slate-50 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 p-5 text-slate-800">
                  <h3 className="text-lg font-semibold">{faq.question}</h3>
                  <svg
                    className="size-5 shrink-0 text-blue-600 transition-transform duration-300 group-open:-rotate-180"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </summary>
                <p className="px-5 pb-5 text-slate-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;